/**
 * The error body the core actually sends.
 *
 * It is RFC 9457 `application/problem+json` **and** carries the protocol's `error` envelope, in
 * one body — see `core/src/Api/Error/ApiProblemSubscriber.php`, which explains why:
 * problem+json because every HTTP tool understands it, `error` because `protocol/openapi.yaml`
 * defines exactly one error shape for both surfaces.
 *
 * This interface is hand-written rather than taken from the generated `Error` schema because the
 * generated schema describes only the problem+json half — API Platform documents the members it
 * knows about, and the `error` member is added by a kernel subscriber it cannot see. That gap is
 * reported to the core rather than papered over here; what this file must not do is pretend the
 * member is absent, because `error.code` is the only machine-readable statement of *what the
 * application decided* and the whole re-auth and capability handling turns on it.
 */
interface ProblemBody {
  type?: string
  title?: string
  status?: number
  detail?: string
  error?: {
    code?: string
    message?: string
    details?: Record<string, unknown>
    request_id?: string | null
  }
}

/**
 * Error codes this client reacts to structurally.
 *
 * The list is deliberately short and the type is deliberately open (`string & {}` keeps
 * autocompletion without closing the union). `code` is an open enumeration on both sides of the
 * protocol: an unrecognised value is displayed, never rejected. A client that narrows it to a
 * closed union starts throwing away errors from a core newer than itself, which is the failure
 * mode the whole versioning policy exists to prevent.
 *
 * The spellings are the server's, from `core/src/Api/Error/ErrorCode.php`. Two of them were
 * previously wrong here, and each wrong spelling silently disabled a behaviour: the client tested
 * for `REAUTH_REQUIRED`, so it never recognised the one refusal it knows how to recover from.
 */
export type ApiErrorCode =
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  /** The session authenticated with a password but has not presented a second factor. */
  | 'MFA_REQUIRED'
  /** A destructive capability was requested without a fresh re-authentication (design §11.4). */
  | 'REAUTHENTICATION_REQUIRED'
  | 'CAPABILITY_UNSUPPORTED'
  | 'CAPABILITY_DISABLED'
  | 'MANIFEST_UNAVAILABLE'
  | 'ASSET_ARCHIVED'
  | 'ASSET_HAS_NO_AGENT'
  | 'OP_CONFLICT'
  | 'ILLEGAL_STATE'
  | 'RATE_LIMITED'
  | 'INVALID_PARAMS'
  | 'NOT_FOUND'
  | 'NOT_IMPLEMENTED'
  | 'INTERNAL'
  | 'UNAVAILABLE'
  // eslint-disable-next-line @typescript-eslint/ban-types
  | (string & {})

/**
 * A structured failure from the core.
 *
 * Carries the HTTP status *and* the protocol error code, because they answer different questions:
 * the status says what the transport did, the code says what the application decided. Collapsing
 * them is how "the site refused" becomes indistinguishable from "the site is broken".
 */
export class ApiError extends Error {
  override readonly name = 'ApiError'

  constructor(
    readonly status: number,
    readonly code: ApiErrorCode,
    message: string,
    readonly details: Record<string, unknown> = {},
    readonly requestId: string | null = null,
    readonly operationId: string | null = null,
  ) {
    super(message)
  }

  /** The session is gone or was never valid. */
  get isUnauthorized(): boolean {
    return this.status === 401
  }

  /** A fresh authentication factor is required before this may be dispatched (design §11.4). */
  get needsReauth(): boolean {
    return this.status === 403 && this.code === 'REAUTHENTICATION_REQUIRED'
  }

  /**
   * The session has a valid token that has not presented a second factor.
   *
   * Design §11.4 makes MFA mandatory across this whole surface, so this is the *normal* answer to
   * every request made between `POST /auth/token` and `POST /auth/mfa/verify` — not a fault, and
   * emphatically not something to route around. The client's job is to drive the second factor;
   * the only endpoints exempt are `/auth/me`, `/auth/mfa/*`, `/auth/refresh` and `/auth/revoke`
   * (`core/src/Api/Security/Mfa/SecondFactorEnforcementSubscriber.php`).
   */
  get needsSecondFactor(): boolean {
    return this.status === 403 && this.code === 'MFA_REQUIRED'
  }

  /**
   * No manifest has ever been fetched, so what the asset can do is *unknown*.
   *
   * Distinct from a manifest that declares nothing, which is a complete answer. Callers must not
   * render "this asset supports nothing" for this.
   */
  get isManifestUnavailable(): boolean {
    return this.code === 'MANIFEST_UNAVAILABLE'
  }

  /**
   * The asset's manifest does not declare the capability this view depends on.
   *
   * Callers render an explanatory absence for this, never an empty list: "this asset cannot tell
   * us" and "there is nothing to tell" are different facts.
   */
  get isCapabilityMissing(): boolean {
    return this.code === 'CAPABILITY_UNSUPPORTED' || this.code === 'CAPABILITY_DISABLED'
  }

  get isNotFound(): boolean {
    return this.status === 404
  }

  /** Seconds the core asked us to wait, when it said. */
  get retryAfter(): number | null {
    const value = this.details.retry_after
    return typeof value === 'number' ? value : null
  }

  static fromResponse(
    status: number,
    body: unknown,
    requestId: string | null,
    operationId: string,
  ): ApiError {
    const problem = body as ProblemBody | undefined
    const payload = problem?.error

    if (payload && typeof payload.code === 'string') {
      return new ApiError(
        status,
        payload.code,
        payload.message ?? problem?.detail ?? 'The request failed.',
        payload.details ?? {},
        payload.request_id ?? requestId,
        operationId,
      )
    }

    // A problem+json body with no `error` member: a validation failure rendered by API Platform
    // itself, or a response from something in front of the core. `detail` is still worth showing;
    // inventing a code is not, so the status decides.
    return new ApiError(
      status,
      status === 401 ? 'UNAUTHORIZED' : status === 404 ? 'NOT_FOUND' : 'INTERNAL',
      typeof problem?.detail === 'string' && problem.detail !== ''
        ? problem.detail
        : defaultMessageFor(status),
      {},
      requestId,
      operationId,
    )
  }
}

/**
 * The request never reached the core, or its answer never came back.
 *
 * Kept separate from {@see ApiError} because the two lead to different words on screen. "The core
 * said no" and "we could not reach the core" are not the same news, and a dashboard that shows
 * one for the other trains its operators to distrust it.
 */
export class NetworkError extends Error {
  override readonly name = 'NetworkError'

  constructor(
    readonly operationId: string,
    override readonly cause: unknown,
  ) {
    super('Could not reach the oui Observe core.')
  }
}

/** The user, or a component unmounting, abandoned the request. Never shown to anyone. */
export class AbortedError extends Error {
  override readonly name = 'AbortedError'

  constructor(readonly operationId: string) {
    super('Request aborted.')
  }
}

const defaultMessageFor = (status: number): string => {
  if (status === 401) return 'Your session has expired.'
  if (status === 403) return 'You are not permitted to do that.'
  if (status === 404) return 'Not found.'
  if (status === 409) return 'That conflicts with the current state.'
  if (status === 429) return 'Too many requests — slow down.'
  if (status >= 500) return 'The core reported an internal error.'
  return 'The request failed.'
}

/** Message safe to put in front of a person, whatever went wrong. */
export const describeError = (error: unknown): string => {
  if (error instanceof ApiError) return error.message
  if (error instanceof NetworkError) return error.message
  if (error instanceof Error) return error.message
  return 'Something went wrong.'
}

export const isAborted = (error: unknown): boolean =>
  error instanceof AbortedError ||
  (error instanceof DOMException && error.name === 'AbortError') ||
  (error instanceof Error && error.name === 'AbortError')
